import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { SessionService } from './shared/services/session.service';
import { NotificationService } from './shared/services/notification.service';
import { SharedModule } from './shared/shared.module';
import { AssignmentDto } from './shared/generated/backend';
import { DxResizableModule, DxToolbarModule, DxMenuModule } from 'devextreme-angular';
import { DxMenuTypes } from 'devextreme-angular/ui/menu';
import { TranslateService } from '@ngx-translate/core';

type AppMenuItem = {
  text: string;
  e2eId: string;
  route?: string[];
  action?: 'login' | 'logout';
  visible?: boolean;
  items?: AppMenuItem[];
};

const ACCOUNT_MENU_E2E_ID = 'account-menu';
const NOTIFICATION_BADGE_E2E_ID = 'notification-badge';
const NOTIFICATION_BADGE_CLASS = 'notification-badge';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SharedModule, DxMenuModule, DxResizableModule, DxToolbarModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'TransportModule';
  env = environment.environmentName;
  readonly showNotificationBanner = signal(false);
  readonly showNotificationErrorBanner = signal(false);
  readonly assignments = signal(new Array<AssignmentDto>());
  readonly loggedIn = signal(false);
  readonly oid = signal('');
  readonly name = signal('');

  menuItems: AppMenuItem[] = [];

  private accountMenuElement?: HTMLElement;

  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  protected location = inject(Location);
  private router = inject(Router);
  private translate = inject(TranslateService);

  readonly notificationCount = this.notificationService.count;
  readonly notificationBadgeText = computed(() => {
    const count = this.notificationCount();
    if (count <= 0) {
      return '';
    }
    return count > 99 ? '99+' : String(count);
  });

  constructor() {
    effect(() => {
      this.notificationBadgeText();
      this.applyNotificationBadge();
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const urlTree = this.router.parseUrl(this.router.url);
      const primaryUrl = urlTree.root.children['primary']?.segments.map(s => s.path).join('/') ?? '';

      if (primaryUrl === 'redirect' || primaryUrl === 'logout' || primaryUrl === 'access-denied') {
        return;
      }

      if (!this.sessionService.isLoggedIn) {
        return;
      }

      const rightOutletActive = urlTree.root.children['right'];

      if (!rightOutletActive) {
        void this.router.navigate([{ outlets: { right: ['project'] } }], {
          queryParamsHandling: 'preserve',
          skipLocationChange: false,
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.updateLoggedInUser();
    this.buildMenuItems();

    this.sessionService.isLoggedIn$.subscribe(() => {
      this.updateLoggedInUser();
      this.buildMenuItems();
      this.refreshNotifications();
    });

    this.translate.onLangChange.subscribe(() => {
      this.buildMenuItems();
    });
  }

  private refreshNotifications(): void {
    if (this.sessionService.isLoggedIn) {
      this.notificationService.refresh();
    } else {
      this.notificationService.reset();
    }
  }

  hasRoleToSeeMasterData(): boolean {
    return this.sessionService.isFleet || this.sessionService.isAdmin || this.sessionService.isOM;
  }

  hasRoleToSeeTourDispo(): boolean {
    return this.sessionService.isAdmin || this.sessionService.isAzubi || this.sessionService.isDispo;
  }

  updateLoggedInUser() {
    this.oid.set(this.sessionService.getOid);
    this.name.set(this.sessionService.getName);
    this.loggedIn.set(this.sessionService.isLoggedIn);
  }

  buildMenuItems(): void {
    const t = (key: string) => this.translate.instant(key);

    const items: AppMenuItem[] = [
      {
        text: t('HEADER.TRANSPORT'),
        e2eId: 'transport-menu',
        items: [
          {
            text: t('HEADER.TOUR_DISPO'),
            e2eId: 'tour-menu-item',
            route: ['/tour'],
            visible: this.hasRoleToSeeTourDispo(),
          },
          {
            text: t('HEADER.VEHICLE_DISPO'),
            e2eId: 'vehicle-dispo-menu-item',
            route: ['/schedule/vehicle'],
          },
          {
            text: t('HEADER.DRIVER_DISPO'),
            e2eId: 'driver-dispo-menu-item',
            route: ['/schedule/driver'],
          },
        ].filter(item => item.visible !== false),
      },
    ];

    if (this.hasRoleToSeeMasterData()) {
      items.push({
        text: t('HEADER.MASTERDATA'),
        e2eId: 'masterdata-menu',
        items: [
          {
            text: t('HEADER.LOADUNITS'),
            e2eId: 'loadunits-menu-item',
            route: ['/masterdata/loadunits'],
          },
          {
            text: t('HEADER.DRIVERS'),
            e2eId: 'drivers-menu-item',
            route: ['/masterdata/driver'],
          },
        ],
      });
    }

    items.push({
      text: t('HEADER.ACCOUNT'),
      e2eId: ACCOUNT_MENU_E2E_ID,
      items: this.loggedIn()
        ? [
            {
              text: t('HEADER.LOGOUT'),
              e2eId: 'logout-menu-item',
              action: 'logout',
            },
          ]
        : [
            {
              text: t('HEADER.LOGIN'),
              e2eId: 'login-menu-item',
              action: 'login',
            },
          ],
    });

    this.menuItems = items;
  }

  onMenuItemRendered(e: DxMenuTypes.ItemRenderedEvent): void {
    const item = e.itemData as AppMenuItem | undefined;
    const element = e.itemElement as HTMLElement | undefined;

    if (!item || !element) {
      return;
    }

    element.setAttribute('data-e2eid', item.e2eId);

    if (item.e2eId === ACCOUNT_MENU_E2E_ID) {
      this.accountMenuElement = element;
      this.applyNotificationBadge();
    }
  }

  /**
   * Adds, updates or removes the notification badge on the cached account menu
   * element based on the current count. Idempotent: never creates duplicates.
   */
  private applyNotificationBadge(): void {
    const element = this.accountMenuElement;
    if (!element) {
      return;
    }

    const badgeText = this.notificationBadgeText();
    let badge = element.querySelector<HTMLElement>(`.${NOTIFICATION_BADGE_CLASS}`);

    if (!badgeText) {
      badge?.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = NOTIFICATION_BADGE_CLASS;
      badge.setAttribute('data-e2eid', NOTIFICATION_BADGE_E2E_ID);
      element.appendChild(badge);
    }

    const count = this.notificationCount();
    badge.textContent = badgeText;
    badge.setAttribute('data-count', String(count));
    badge.title = this.translate.instant(
      count === 1 ? 'HEADER.NOTIFICATIONS_TOOLTIP_SINGULAR' : 'HEADER.NOTIFICATIONS_TOOLTIP',
      { count }
    );
  }

  async onMenuItemClick(e: DxMenuTypes.ItemClickEvent): Promise<void> {
    const item = e.itemData as AppMenuItem | undefined;
    if (!item) {
      return;
    }

    if (item.route) {
      await this.router.navigate(item.route);
      return;
    }

    if (item.action === 'login') {
      await this.login();
      return;
    }

    if (item.action === 'logout') {
      await this.logout();
    }
  }

  async toggleLogin() {
    const isLoggedIn = this.sessionService.isLoggedIn;
    if (isLoggedIn) {
      await this.sessionService.logout();
    } else {
      await this.sessionService.login();
    }
  }

  async login() {
    await this.sessionService.login();
  }

  async logout() {
    await this.sessionService.logout();
  }
}
